import BreadCrumb from "Common/BreadCrumb";
import { useFormik as useFormic } from "formik";
import { Loader, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import * as Yup from "yup";
import { useSync } from "../../context/SyncContext";
import useLocalStorageSettings from "../../hooks/useLocalStorageSettings";

interface SettingsFormValues {
  updateEvery: number | string;
}

const Settings = () => {
  const { settings, saveSettings, isLoading } = useLocalStorageSettings();
  const { syncState, syncNow } = useSync();
  const { isSyncing, lastSyncAt, pendingCount, syncError } = syncState;
  const navigate = useNavigate();

  const validation = useFormic<SettingsFormValues>({
    initialValues: {
      updateEvery: settings.updateEvery || 60,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      updateEvery: Yup.number()
        .min(1, "Deve ser no mínimo 1 minuto")
        .max(1440, "Deve ser no máximo 1440 minutos (24 horas)")
        .required("Por favor insira o tempo de atualização"),
    }),
    onSubmit: async (values) => {
      try {
        const updateEveryValue =
          typeof values.updateEvery === "string"
            ? Number.parseInt(values.updateEvery) || 60
            : values.updateEvery;
        const saved = await saveSettings({
          updateEvery: updateEveryValue,
        });

        if (saved) {
          toast.success("Configurações salvas com sucesso!", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          throw new Error("Erro ao salvar configurações");
        }
      } catch (error) {
        console.error("Erro ao salvar:", error);
        toast.error("Erro ao salvar configurações. Tente novamente.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    },
  });

  const handleCancel = () => {
    navigate("/");
  };

  return (
    <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
      <BreadCrumb title="Configurações" />
      <div className="grid grid-cols-1 gap-x-5">
        <div className="card">
          <div className="card-body">
            <form onSubmit={validation.handleSubmit} className="space-y-4">
              <div className="mb-3 xl">
                <label htmlFor="updateEvery" className="inline-block mb-2 text-base font-medium">
                  Atualizar a cada (minutos): <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="updateEvery"
                  id="updateEvery"
                  aria-describedby="updateEvery-help updateEvery-error"
                  aria-invalid={!!(validation.touched.updateEvery && validation.errors.updateEvery)}
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="60"
                  value={validation.values.updateEvery}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    if (rawValue === "") {
                      validation.setFieldValue("updateEvery", "");
                      return;
                    }
                    const value = Number.parseInt(rawValue);
                    if (!Number.isNaN(value) && value >= 1 && value <= 1440) {
                      validation.setFieldValue("updateEvery", value);
                    }
                  }}
                  onBlur={validation.handleBlur}
                  min={1}
                  max={1440}
                  step={1}
                />
                <p id="updateEvery-help" className="mt-1 text-sm text-slate-500 dark:text-zink-400">
                  Configure o intervalo de atualização e sincronização automática (1 a 1440 minutos
                  / 24 horas)
                </p>
                {validation.touched.updateEvery && validation.errors.updateEvery ? (
                  <p id="updateEvery-error" className="mt-1 text-sm text-red-500">
                    {typeof validation.errors.updateEvery === "string"
                      ? validation.errors.updateEvery
                      : "Erro de validação"}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full text-white sm:w-auto btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-2 -ml-1 text-white animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Salvando...
                    </div>
                  ) : (
                    "Salvar"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-full sm:w-auto text-slate-500 btn bg-slate-200 border-slate-200 hover:text-slate-600 hover:bg-slate-300 hover:border-slate-300 focus:text-slate-600 focus:bg-slate-300 focus:border-slate-300 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-300 active:border-slate-300 active:ring active:ring-slate-100 dark:bg-zink-600 dark:hover:bg-zink-500 dark:border-zink-600 dark:hover:border-zink-500 dark:text-zink-200 dark:ring-zink-400/10"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-x-5 mt-5">
        <div className="card">
          <div className="card-body">
            <h5 className="mb-4 text-base font-semibold text-slate-700 dark:text-slate-200">
              Sincronização de Pedidos
            </h5>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Pedidos pendentes:{" "}
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {pendingCount}
                  </span>
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Última sincronização:{" "}
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {lastSyncAt
                      ? new Date(lastSyncAt).toLocaleString("pt-BR")
                      : "Nunca"}
                  </span>
                </p>
                {syncError && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Erro: {syncError}
                  </p>
                )}
              </div>

              <button
                type="button"
                disabled={isSyncing || pendingCount === 0}
                onClick={async () => {
                  await syncNow();
                  if (!syncState.syncError) {
                    toast.success("Sincronização concluída!", {
                      position: "top-right",
                      autoClose: 3000,
                    });
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-white border rounded-md bg-custom-500 border-custom-500 hover:bg-custom-600 hover:border-custom-600 focus:outline-none focus:ring-2 focus:ring-custom-100 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:ring-custom-400/20"
              >
                {isSyncing ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {isSyncing ? "Sincronizando..." : "Sincronizar Agora"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default Settings;
